import React, { useState, useEffect, useRef } from 'react';
import { FaSearch, FaTimes, FaBars, FaBell } from 'react-icons/fa';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import Profile from '../../components/profile/profile';
import axios from 'axios';
import API_BASE_URL from '../../components/apiconfig/api-config';

const Navbar = ({ sidebarWidth, sidebarOpen, setSidebarOpen, isMobile }) => {
  const [showProfilePanel, setShowProfilePanel] = useState(false);
  const [userName, setUserName] = useState('User');
  const [contractNo, setContractNo] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const debounceTimeout = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    fetchUserProfile();
  }, []);

  const fetchUserProfile = async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/profile/profile-get`,
        { withCredentials: true }
      );
      if (response.data.success) {
        setUserName(response.data.data.name);
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
    }
  };

  const fetchNotifications = async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/notifications/active`,
        { withCredentials: true }
      );
      if (response.data && response.data.success) {
        setNotifications(response.data.notifications || []);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  useEffect(() => {
    fetchNotifications();
    // Refresh notifications every 5 minutes
    const interval = setInterval(fetchNotifications, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const fetchSuggestions = (value) => {
    if (!value) {
      setSuggestions([]);
      return;
    }
    fetch(`${API_BASE_URL}/api/report/master-report?purchase_contract_no=`, {
      credentials: "include",
    })
      .then((res) => res.json())
      .then((contracts) => {
        if (!Array.isArray(contracts)) {
          setSuggestions([]);
          return;
        }
        const filtered = contracts.filter(
          (c) =>
            c.purchase_contract_no &&
            c.purchase_contract_no.toLowerCase().includes(value.toLowerCase())
        );
        setSuggestions(filtered);
      })
      .catch(() => setSuggestions([]));
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setContractNo(value);
    setShowSuggestions(true);
    if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
    debounceTimeout.current = setTimeout(() => {
      fetchSuggestions(value);
    }, 200);
  };

  const handleSuggestionClick = (contract) => {
    setContractNo(contract.purchase_contract_no);
    setShowSuggestions(false);
    setSuggestions([]);
    navigate(`/report/master-report?contract=${encodeURIComponent(contract.purchase_contract_no)}`);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (contractNo.trim()) {
      setShowSuggestions(false);
      setSuggestions([]);
      navigate(`/report/master-report?contract=${encodeURIComponent(contractNo.trim())}`);
    }
  };

  const handleBlur = () => {
    setTimeout(() => setShowSuggestions(false), 150);
  };

  const handleClosePanel = () => {
    setShowProfilePanel(false);
    fetchUserProfile();
  };

  // Generate breadcrumbs from location
  const pathnames = location.pathname.split('/').filter(x => x);
  
  return (
    <>
      <nav
        className="fixed top-0 z-30 flex items-center justify-between px-3 h-14 bg-white border-b border-gray-200 transition-all duration-300"
        style={{ 
          marginLeft: isMobile ? 0 : sidebarWidth, 
          width: isMobile ? '100%' : `calc(100% - ${sidebarWidth}px)` 
        }}
      >
        <div className="flex items-center gap-3 w-full max-w-full overflow-hidden">
          {isMobile && (
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-1.5 rounded-md hover:bg-gray-100 text-gray-500 transition-colors shrink-0"
            >
              <FaBars className="text-[15px]" />
            </button>
          )}

          {/* Breadcrumbs */}
          <div className="hidden md:flex items-center gap-2 text-[14px] font-medium text-gray-400 whitespace-nowrap overflow-hidden">
            <Link to="/dashboard" className="hover:text-gray-900 transition-colors shrink-0">CTMS Module</Link>
            {pathnames.length > 0 && <span className="shrink-0">›</span>}
            {pathnames.length > 1 && (
              <>
                <span className="shrink-0">...</span>
                <span className="shrink-0">›</span>
              </>
            )}
            {pathnames.length > 0 && (
              <span className="text-gray-800 font-semibold truncate">
                {pathnames[pathnames.length - 1].replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 sm:gap-3 ml-4 shrink-0">
          {/* Search Bar */}
          <div className="relative hidden sm:block w-48 lg:w-64">
            <form onSubmit={handleSearch}>
              <div className="relative flex items-center">
                <FaSearch className="absolute left-2.5 text-gray-400 text-[14px]" />
                <input
                  type="text"
                  placeholder="Search contract..."
                  value={contractNo}
                  onChange={handleInputChange}
                  onFocus={() => contractNo && setShowSuggestions(true)}
                  onBlur={handleBlur}
                  className="w-full bg-gray-50/50 hover:bg-gray-100/50 border border-transparent hover:border-gray-200 focus:border-gray-300 focus:bg-white focus:ring-4 focus:ring-gray-100 transition-all rounded-md px-3 py-1.5 pl-8 text-[14px] text-gray-700 placeholder-gray-400 outline-none h-8"
                />
                {contractNo && (
                  <button
                    type="button"
                    onClick={() => setContractNo('')}
                    className="absolute right-2 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <FaTimes className="text-[10px]" />
                  </button>
                )}
              </div>
            </form>
            {showSuggestions && suggestions.length > 0 && (
              <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-xl shadow-gray-200/50 max-h-48 overflow-y-auto custom-scrollbar">
                {suggestions.map((c) => (
                  <div
                    key={c.purchase_contract_no}
                    className="px-3 py-1.5 cursor-pointer hover:bg-gray-50 text-gray-700 text-[14px] border-b border-gray-100 last:border-b-0"
                    onMouseDown={() => handleSuggestionClick(c)}
                  >
                    {c.purchase_contract_no}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="h-4 w-px bg-gray-200 mx-1 hidden sm:block"></div>

          {/* Icon buttons */}
          <div className="relative">
            <button 
              onClick={() => {
                setShowNotifications(!showNotifications);
                if (showProfilePanel) setShowProfilePanel(false);
              }}
              className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors relative" 
              title="Notifications"
            >
              <FaBell className="text-[14px]" />
              {notifications.length > 0 && (
                <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full border border-white"></span>
              )}
            </button>

            {/* Notifications Panel */}
            {showNotifications && (
              <div className="absolute top-10 right-0 w-80 bg-white rounded-lg shadow-xl shadow-gray-200/50 border border-gray-200 z-50 overflow-hidden">
                <div className="p-3 border-b border-gray-100 bg-gray-50 flex justify-between items-center">
                  <h3 className="text-[14px] font-bold text-gray-800">Notifications</h3>
                  <span className="text-xs bg-red-100 text-red-700 font-bold px-2 py-0.5 rounded-full">{notifications.length} New</span>
                </div>
                <div className="max-h-80 overflow-y-auto custom-scrollbar">
                  {notifications.length > 0 ? (
                    <div className="divide-y divide-gray-100">
                      {notifications.map((notif) => (
                        <div key={notif.id} className="p-3 hover:bg-gray-50 transition-colors cursor-default">
                          <div className="flex justify-between items-start mb-1">
                            <span className={"text-[11px] font-bold px-2 py-0.5 rounded-sm uppercase tracking-wider " + (
                              notif.type === 'Pending Dispatch' ? 'bg-orange-100 text-orange-700' :
                              notif.type === 'Payment Reminder' ? 'bg-red-100 text-red-700' :
                              'bg-purple-100 text-purple-700'
                            )}>
                              {notif.type}
                            </span>
                            <span className="text-[10px] text-gray-400">
                              {new Date(notif.date).toLocaleDateString('en-GB')}
                            </span>
                          </div>
                          <p className="text-[13px] font-semibold text-gray-800 mt-1">{notif.title}</p>
                          <p className="text-[12px] text-gray-600 mt-0.5">{notif.message}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="p-6 text-center text-gray-500 text-[13px]">
                      No new notifications
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="h-4 w-px bg-gray-200 mx-1"></div>

          {/* User Profile */}
          <button 
            onClick={() => {
              setShowProfilePanel(!showProfilePanel);
              if (showNotifications) setShowNotifications(false);
            }}
            className="w-8 h-8 rounded bg-gray-100 border border-gray-200 flex items-center justify-center text-gray-600 hover:bg-gray-200 transition-colors shrink-0"
            title="Profile"
          >
            <span className="text-[12px] font-bold">{userName.charAt(0).toUpperCase()}</span>
          </button>
        </div>

        {/* Profile Panel Dropdown */}
        {showProfilePanel && (
          <div className="absolute top-12 right-3 w-72 bg-white rounded-lg shadow-xl shadow-gray-200/50 border border-gray-200 z-50">
            <div className="p-3">
              <div className="flex items-center justify-between mb-3 border-b border-gray-100 pb-2">
                <h3 className="text-[14px] font-semibold text-gray-900">Account</h3>
                <button
                  onClick={handleClosePanel}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <FaTimes className="text-[14px]" />
                </button>
              </div>
              <Profile onClose={handleClosePanel} />
            </div>
          </div>
        )}
      </nav>

      {/* Backdrop for panels */}
      {(showProfilePanel || showNotifications) && (
        <div 
          className="fixed inset-0 z-20"
          onClick={() => {
            setShowProfilePanel(false);
            setShowNotifications(false);
          }}
        ></div>
      )}
    </>
  );
};

export default Navbar;